import { lazy } from "react";

const CHUNK_RELOAD_COUNT_KEY = "chunk_reload_count";
const LEGACY_CHUNK_RELOADED_KEY = "chunk_reloaded";
/** Delayed-retry counter used once the immediate reload budget is spent. */
export const CHUNK_BACKOFF_ATTEMPT_KEY = "chunk_backoff_attempt";
const MAX_CHUNK_RELOADS = 2;

/** Missing or mismatched lazy chunks after a deploy (Parcel often surfaces these as `Cannot find module '…'`). */
export const isChunkLoadError = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error ?? "");
    if (!message) return false;
    return (
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("Loading chunk") ||
        message.includes("Importing a module script failed") ||
        message.includes("Loading module") ||
        message.includes("Failed to load module") ||
        message.includes("dynamically imported module") ||
        message.includes("Cannot find module") ||
        /Cannot find module\s+['"][^'"]+['"]/i.test(message) ||
        (error instanceof Error && error.name === "ChunkLoadError") ||
        (typeof error === "object" &&
            error != null &&
            "code" in error &&
            (error as { code?: string }).code === "CSS_CHUNK_LOAD_FAILED")
    );
};

/** Hard navigation with cache-bust so CDN/browser cannot serve a mixed old+new chunk graph. */
export const reloadForFreshChunks = (): boolean => {
    if (typeof window === "undefined") return false;
    const count = parseInt(sessionStorage.getItem(CHUNK_RELOAD_COUNT_KEY) || "0", 10);
    if (count >= MAX_CHUNK_RELOADS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_COUNT_KEY, String(count + 1));
    sessionStorage.removeItem(LEGACY_CHUNK_RELOADED_KEY);
    const url = new URL(window.location.href);
    stripChunkCacheBustSearchParam(url.searchParams);
    url.searchParams.set("_cb", String(Date.now()));
    window.location.replace(url.toString());
    return true;
};

/** Cleared only after the app has been stable — not on first paint (that caused infinite `_cb` reload loops). */
export function clearChunkReloadSessionFlags(): void {
    if (typeof window === "undefined") return;
    sessionStorage.removeItem(CHUNK_RELOAD_COUNT_KEY);
    sessionStorage.removeItem(LEGACY_CHUNK_RELOADED_KEY);
    sessionStorage.removeItem(CHUNK_BACKOFF_ATTEMPT_KEY);
}

/** Drop Parcel cache-bust param so invite / routing logic does not see a changing `search` every reload. */
export function stripChunkCacheBustSearchParam(params: URLSearchParams): void {
    params.delete("_cb");
}

const ASSET_URL_RE = /https?:\/\/[^\s"'()]+\.(?:js|css)(?:\?[^\s"'()]*)?/gi;
const REFETCH_TIMEOUT_MS = 4_000;

function failedSameOriginAssetUrls(error: unknown): string[] {
    if (typeof window === "undefined") return [];
    const urls = new Set<string>();
    const message = error instanceof Error ? error.message : String(error ?? "");
    for (const match of message.match(ASSET_URL_RE) ?? []) urls.add(match);
    try {
        for (const entry of performance.getEntriesByType("resource") as PerformanceResourceTiming[]) {
            const status = (entry as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;
            if (status != null && status >= 400 && /\.(?:js|css)(?:\?|$)/i.test(entry.name)) {
                urls.add(entry.name);
            }
        }
    } catch {
        /* ignore */
    }
    return [...urls].filter((url) => {
        try {
            return new URL(url).origin === window.location.origin;
        } catch {
            return false;
        }
    });
}

/**
 * The CDN tells browsers to cache 404s for a day. A chunk that 404'd mid-deploy then stays
 * "missing" across every reload even after it is back on the server. Re-fetching with
 * `cache: "reload"` bypasses and overwrites that cached 404 before we navigate.
 */
export async function refreshFailedChunkCache(error: unknown): Promise<void> {
    const urls = failedSameOriginAssetUrls(error);
    if (!urls.length) return;
    await Promise.race([
        Promise.allSettled(urls.map((url) => fetch(url, { cache: "reload", credentials: "same-origin" }))),
        new Promise((resolve) => window.setTimeout(resolve, REFETCH_TIMEOUT_MS)),
    ]);
}

/**
 * Try a hard reload for fresh chunks. If the reload budget is exhausted, rethrow so Suspense
 * can exit (a never-resolving promise left the app stuck on a purple shell until manual refresh).
 */
const handleChunkError = async (error: unknown): Promise<never> => {
    await refreshFailedChunkCache(error);
    if (reloadForFreshChunks()) {
        // Navigation in progress — park until the document unloads.
        return new Promise(() => {});
    }
    throw error instanceof Error ? error : new Error(String(error ?? "Chunk load failed"));
};

/** Catch lazy-import failures that escape React error boundaries. */
export function registerChunkLoadRecovery(): () => void {
    if (typeof window === "undefined") return () => {};
    const onRejection = (event: PromiseRejectionEvent) => {
        if (!isChunkLoadError(event.reason)) return;
        event.preventDefault();
        void refreshFailedChunkCache(event.reason).finally(() => {
            reloadForFreshChunks();
        });
    };
    window.addEventListener("unhandledrejection", onRejection);
    return () => window.removeEventListener("unhandledrejection", onRejection);
}

// Wrapper for lazy imports that handles chunk loading failures by refreshing the page
// This is useful for old browser tabs where chunks are missing after a deployment
export const retryLazyImport = (importFn: () => Promise<unknown>) => {
    return lazy(() => {
        return importFn().catch((error) => {
            if (isChunkLoadError(error) && typeof window !== "undefined") {
                return handleChunkError(error);
            }
            throw error;
        });
    });
};
